/**
 * v3.tasks.create — Wave 14 (Phase 1.5) Task Creation + Management
 *
 * Backed by PCBJxChangeApi/Functions/TaskCreateFunction.cs +
 * RecurringTaskMaterializerFunction.cs:
 *   POST   /v3/tasks/create-adhoc                 (any officer)
 *   POST   /v3/tasks/recurring                    (Supervisor / Manager)
 *   GET    /v3/tasks/recurring?createdBy=...       (list my schedules)
 *   DELETE /v3/tasks/recurring/{parentId}          (stop materializing)
 *   GET    /v3/tasks/management/dashboard          (Supervisor team rollup)
 *
 * Routes that require the Supervisor / Manager role are gated server-side
 * by AdminAuthService Path C (officer-code header → DB role lookup) — the
 * same gate every other v3 admin route uses today.
 *
 * Composed onto TasksApi as `client.retail.tasks.create.adHoc(...)` etc via
 * module augmentation in client.ts (mirrors the WEEKLY-TASKS-BUILD
 * augmentation pattern in v3-tasks-weekly.ts).
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';

// ─────────────────────────────────────────────────────────────────────────
// Wire shapes
// ─────────────────────────────────────────────────────────────────────────

export type TaskCreatePriority = 'P1' | 'P2' | 'P3' | 'Critical' | 'High' | 'Normal' | 'Low';

/**
 * Task summary returned from create-adhoc / create-recurring / list-recurring.
 * Slightly looser than `TaskRecordDto` because the create paths return the
 * Wave-14 columns (recurrence + group-claim) inline for the UI.
 */
export interface TaskCreateSummary {
  taskId: string;
  title: string;
  type: string;
  description?: string | null;
  priority: string;
  status: string;
  category?: string | null;
  dueDate?: string | null;
  assignedTo?: string | null;
  groupAssignmentOfficerCodes: ReadonlyArray<string>;
  createdByOfficerCode?: string | null;
  cartId?: string | null;
  cifId?: string | null;
  isRecurringTemplate: boolean;
  recurrencePatternRrule?: string | null;
  recurrenceParentId?: string | null;
  autoCreated: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdHocTaskInput {
  title: string;
  description?: string;
  priority: TaskCreatePriority;
  dueDate?: string;
  assignedTo?: string;
  type?: string;
  category?: string;
  cartId?: string;
  cifId?: string;
  /** Required when caller didn't include `x-pcb-officer-code` on the http client. */
  createdByOfficerCode?: string;
  /** First-to-act group claim — array of officerCodes. */
  groupAssignmentOfficerCodes?: ReadonlyArray<string>;
}

export interface CreateRecurringTaskInput {
  title: string;
  description?: string;
  priority: TaskCreatePriority;
  type?: string;
  category?: string;
  /** HH:mm — applied to every materialized instance's dueDate. Default 09:00 server-side. */
  dueTimeOffset?: string;
  assignedTo?: string;
  groupAssignmentOfficerCodes?: ReadonlyArray<string>;
  /** RFC 5545 RRULE; supports FREQ=DAILY|WEEKLY|MONTHLY + BYDAY/BYMONTHDAY only. */
  recurrencePatternRrule: string;
  startDate: string;
  endDate?: string;
  createdByOfficerCode?: string;
}

export interface RecurringScheduleSummary extends Omit<TaskCreateSummary,
  'isRecurringTemplate' | 'autoCreated'> {
  /** Always true for rows returned from the list endpoint. */
  startDate?: string | null;
  /** Live-evaluated by the server — next date the materializer would fire. */
  nextOccurrence?: string | null;
}

export interface ListMyRecurringResponse {
  success: boolean;
  count: number;
  schedules: ReadonlyArray<RecurringScheduleSummary>;
}

export interface CreateTaskResponse {
  success: boolean;
  task?: TaskCreateSummary;
  error?: string;
}

export interface StopRecurringResponse {
  success: boolean;
  taskId?: string;
  alreadyCancelled?: boolean;
  error?: string;
}

export interface ManagementDashboardOfficer {
  officerCode: string | null;
  upn: string | null;
  displayName: string;
  openCount: number;
  overdueCount: number;
  doneThisWeek: number;
}

export interface ManagementDashboardResponse {
  success: boolean;
  generatedAt: string;
  scope: 'all-officers' | 'my-team';
  officers: ReadonlyArray<ManagementDashboardOfficer>;
  note?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-client
// ─────────────────────────────────────────────────────────────────────────

export class TasksCreateApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /** Officer-driven ad-hoc task. Function-key + officerCode (header or body). */
  public adHoc(
    body: CreateAdHocTaskInput,
    opts?: RequestOptions,
  ): Promise<CreateTaskResponse> {
    return this.http.post<CreateTaskResponse>('/v3/tasks/create-adhoc', {
      ...opts,
      body,
    });
  }

  /**
   * Supervisor / Manager — create a recurring template. The materializer
   * timer spawns instances at 06:00 the day before each occurrence.
   *
   * Authorization: Path C — `x-pcb-officer-code` header attached by the
   * api-client http transport, validated server-side by AdminAuthService
   * against UserRoleAssignments.
   */
  public recurring(
    body: CreateRecurringTaskInput,
    opts?: RequestOptions,
  ): Promise<CreateTaskResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, undefined);
    return this.http.post<CreateTaskResponse>('/v3/tasks/recurring', {
      ...opts,
      headers,
      body,
    });
  }

  public listMyRecurring(
    createdBy: string,
    opts?: RequestOptions,
  ): Promise<ListMyRecurringResponse> {
    return this.http.get<ListMyRecurringResponse>('/v3/tasks/recurring', {
      ...opts,
      query: { createdBy },
    });
  }

  public stopRecurring(
    parentId: string,
    opts?: RequestOptions,
  ): Promise<StopRecurringResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, undefined);
    return this.http.del<StopRecurringResponse>(
      `/v3/tasks/recurring/${encodeURIComponent(parentId)}`,
      { ...opts, headers },
    );
  }

  public dashboard(
    opts?: RequestOptions,
  ): Promise<ManagementDashboardResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, undefined);
    return this.http.get<ManagementDashboardResponse>(
      '/v3/tasks/management/dashboard',
      { ...opts, headers },
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Module augmentation — extend TasksApi with `.create`.
// Mirrors the .weekly augmentation in v3-tasks-weekly.ts so callers do
// `client.retail.tasks.create.adHoc(...)` without breaking existing calls.
// ─────────────────────────────────────────────────────────────────────────

declare module './tasks.js' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  interface TasksApi {
    /**
     * Wave 14 (Phase 1.5) — typed task creation surface.
     *
     * Named `creation` (not `create`) because TasksApi already exposes a
     * `create(req)` method for the legacy POST /v3/tasks system path. The
     * Wave-14 path collection lives under a separate namespace.
     */
    readonly creation: TasksCreateApi;
  }
}
